import type {
  QueryKey,
  UseInfiniteQueryResult,
  UseQueryResult,
} from '@unifyapps/network/react-query';
import type { LocationSensorState } from 'react-use/lib/useLocation';
import type { BlockStateUnionType, BlockType } from '@unifyapps/defs/types/block';
import type { PageVariableState } from '@unifyapps/defs/types/pageVariable';
import type { PageFunctionState } from '@unifyapps/defs/types/pageFunction';
import type {
  DesktopVariant,
  DeviceVariantType,
  MobileVariant,
  TabletVariant,
} from '@unifyapps/defs/types/deviceVariant';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import type { MutableRefObject, RefObject } from 'react';
import type { UserPermissionsMap } from '../../access-control/types';
import type createObjectSetters from '../../../utils/setters/createObjectSetters';
import type { RemountFunction } from '../../../components/Remount/Remount';
import type { InterfaceClient, InterfaceModes } from '../InterfaceStore';
import type { BuildDependencyGraph } from '../../../dependency/manager/types';
import type { BuildDependencyGraphItem } from '../../../dependency/utils/buildDependencyGraph';
import type { DependencyGraphStoreRefType } from '../../context/DependencyGraphContext/types';
import type { UseNoCodeDependencyGraphManagerType } from '../../context/DependencyGraphContext/hooks/useNoCodeDependencyGraphManager';
import type { DeviceVariantActionProps } from './actions/deviceVariantAction';
import type { InterfaceSessionStorageActionProps } from './actions/getInterfaceSessionStorageAction';

type QueryResult = UseQueryResult<Awaited<unknown>, unknown>;

export type DeviceDetailsType = {
  device: DeviceVariantType;
  mobileVariant?: MobileVariant;
  desktopVariant?: DesktopVariant;
  tabletVariant?: TabletVariant;
};

export type QueryRequestResult = {
  queryKey?: QueryKey;
  objects?: unknown[];
  rawData?: unknown;
} & Partial<Pick<QueryResult, 'error' | 'isLoading' | 'isFetching' | 'data'>> &
  Partial<
    Pick<UseInfiniteQueryResult, 'isFetchingNextPage' | 'hasNextPage' | 'fetchNextPage' | 'refetch'>
  >;

type DataSourcesResultsSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'dataSources', QueryRequestResult>
>;

type ManualDataSourcesResultsSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'manualDataSources', QueryRequestResult>
>;

type BlockSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'blocks', BlockStateUnionType>
>;

type PageVariableSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'pageVariables', PageVariableState>
>;

type PageFunctionSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'pageFunctions', PageFunctionState>
>;

type BlocksRefSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'blockRefs', RefObject<unknown>>
>;

type EnvVariablesSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'envVariables', string>
>;

type BlockLifecycleSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'blockLifecycles', BlockLifecycleStatus>
>;

export type ManuallyTriggeredDataSourcesSetterType = ReturnType<
  typeof createObjectSetters<
    GlobalStateStoreType,
    'manuallyTriggeredDataSources',
    ManuallyTriggeredDataSourceType
  >
>;

export type LocationInStore = Pick<
  LocationSensorState,
  'href' | 'hostname' | 'pathname' | 'search' | 'hash'
> & {
  pathParams?: Record<string, string | string[] | undefined>;
};

type LocationSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'location', string>
>;

type ContextualDialogForBlocksSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'contextualDialogForBlocks', string>
>;

export enum GeoLocationStatus {
  Granted = 'GRANTED',
  Disabled = 'DISABLED',
  UserDenied = 'USER_DENIED',
}

export type UserExtraGeoLocationContext = {
  isMocked?: boolean;
  status?: GeoLocationStatus | null;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
};

export type MobileUserExtraContext = {
  safeArea: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  device: {
    isIOS: boolean;
    isAndroid: boolean;
    isRooted: boolean;
    brand: string;
    os: { name: string; version: string };
  };
  geography?: UserExtraGeoLocationContext;
};

export type WebUserExtraContext = {
  device: { brand: string; os: { name: string; version: string } };
  browser: { name: string; version: string };
  geography?: UserExtraGeoLocationContext;
};

export type UserExtraContext = MobileUserExtraContext | WebUserExtraContext;

export type UserContextInStore = {
  id?: string;
  locale: string;
  context?: UserExtraContext;
};

type UserContextSetterType = ReturnType<
  typeof createObjectSetters<GlobalStateStoreType, 'userContext', string>
>;

export type DataSourceMetaType = {
  aggregationMetadata?: QueryRequestResult | undefined;
  previewResult?: QueryRequestResult | undefined;
};

export type GlobalStateStoreFlows = {
  importObjectRecords?: {
    onSuccess?: (...args: unknown[]) => unknown;
    onError?: (...args: unknown[]) => unknown;
    onClose?: (...args: unknown[]) => unknown;
    objectId?: string;
    objectInputs?: {
      key: string;
      value: string;
    }[];
  };
  shareEntityRecord?: {
    onSuccess?: (...args: unknown[]) => unknown;
    onError?: (...args: unknown[]) => unknown;
    onClose?: (...args: unknown[]) => unknown;
    entityId?: string;
    entityType?: string;
  };
  controlMediaViewer?: {
    onSuccess?: (...args: unknown[]) => unknown;
    onError?: (...args: unknown[]) => unknown;
    onClose?: (...args: unknown[]) => unknown;
    url?: string;
    caption?: string;
    mimeType?: string;
    allowDownload?: boolean;
  };
};

export type SetPreBuiltFlowParams =
  | {
      type: 'importObjectRecords';
      data: GlobalStateStoreFlows['importObjectRecords'];
    }
  | {
      type: 'shareEntityRecord';
      data: GlobalStateStoreFlows['shareEntityRecord'];
    }
  | {
      type: 'controlMediaViewer';
      data: GlobalStateStoreFlows['controlMediaViewer'];
    };

export type ManuallyTriggeredDataSourceType = {
  callback: ((...args: unknown[]) => unknown) | undefined;
  dataSourceId: string;
  queryKeyPrefix?: string;
};

export const enum DependencyFlowStatus {
  Staging = 'staging',
  Commit = 'commit',
  Finished = 'finished',
}

export const enum BlockLifecycleStatus {
  Staging = 'staging',
  Rendered = 'rendered',
  Unmounted = 'unmounted',
}

export type DependencyFlowInstanceType = Record<
  string,
  { status: DependencyFlowStatus } | undefined
>;

type DependencyFlowManagerType = {
  dataSources: DependencyFlowInstanceType;
};

export type GlobalStateStoreInitialValue = Pick<
  GlobalStateStore,
  | 'deviceDetails'
  | 'pageInputs'
  | 'userContext'
  | 'manuallyTriggeredDataSources'
  | 'location'
  | 'blocks'
  | 'pageVariables'
  | 'permissions'
  | 'pageFunctions'
  | 'contextualDialogForBlocks'
  | 'isNavigationDrawerOpen'
>;

export type GlobalStateStoreRefType = {
  updateBlocks: ({
    added,
    deleted,
    changed,
  }: {
    added: BlockType[];
    deleted: BlockType[];
    changed: BlockType[];
  }) => void;
};

export type BaseGlobalStateStoreProviderProps = {
  blocks?: InterfacePageEntity['properties']['blocks'];
  pageVariables?: InterfacePageEntity['properties']['pageVariables'];
  deviceDetails: DeviceDetailsType;
  dataSources?: Record<string, DataSourceEntity | undefined>;
  pageInputs?: Record<string, unknown>;
  mode?: InterfaceModes;
  interfaceId: string;
  pageFunctions?: InterfacePageEntity['properties']['pageFunctions'];
  globalStateStoreRef?: MutableRefObject<GlobalStateStoreRefType | undefined>;
  client: InterfaceClient;
  remount: () => void;
  permissions: UserPermissionsMap;
  getDependencyGraphActions: () => DependencyGraphStoreRefType &
    UseNoCodeDependencyGraphManagerType['actions'];
  locale?: string;
};

export type InterfaceSessionStorageType = Partial<
  Pick<QueryResult, 'error' | 'isLoading' | 'isFetching' | 'data'>
>;

export type WithRemountProps = Omit<BaseGlobalStateStoreProviderProps, 'remount' | 'permissions'>;
export type WithPermissionsProps = Omit<BaseGlobalStateStoreProviderProps, 'permissions'>;

export type GlobalStateStore = {
  dataSources: Record<string, QueryRequestResult | undefined>;
  // tentative manual registry, where we keep the inflight manual data sources response, once the response is received, we move it to dataSources, here the key will be the requestId
  manualDataSources: Record<string, QueryRequestResult | undefined>;
  dataSourcesMeta: Record<string, DataSourceMetaType | undefined>;
  blocks: Record<string, BlockStateUnionType | undefined>;
  pageVariables: Record<string, PageVariableState | undefined>;
  pageFunctions: Record<string, PageFunctionState | undefined>;
  manuallyTriggeredDataSources: Record<string, ManuallyTriggeredDataSourceType>;
  /**
   * previewTriggeredDataSources is used to track whether a data source has been triggered for preview
   */
  previewTriggeredDataSources: Record<string, boolean | undefined>;
  /**
   * runTriggeredDataSources is used to track whether a data source has been triggered for run
   * this run is user clicking on the run button when saving the data source, it helps us in deciding the content to be shown in the data source preview panel
   */
  runTriggeredDataSources: Record<string, boolean | undefined>;
  pageInputs: Partial<Record<string, unknown>>;
  location: LocationInStore;
  userContext: UserContextInStore;
  blockRefs: Record<string, RefObject<unknown> | undefined>;
  /**
   * when there is an attempt to render a block, i.e \<BlockRenderer id=\{blockId\} /\>, was called then the block is considered to be attempted to be mounted
   */
  blockLifecycles: Record<string, BlockLifecycleStatus | undefined>;

  flows: GlobalStateStoreFlows;
  deviceDetails: DeviceDetailsType;
  isNavigationDrawerOpen: boolean;
  permissions: UserPermissionsMap;
  interfaceSessionStorage: InterfaceSessionStorageType;
  //  a map of blockId and associated contextual dialog block id
  contextualDialogForBlocks: Record<string, string | undefined>;
  /**
   * intra dependencies are the dependencies that track within the same entity -
   * for example, a data source can have dependencies on other data sources
   */
  intraDependencies: {
    dataSources?: BuildDependencyGraph;
  };
  /**
   * dependency flow manager is used to manage an id, and it's flow status, whether it's in staging, commit or finished
   */
  dependencyFlowManager: DependencyFlowManagerType;
  /**
   * mapping for envrionment variables and their values
   */
  envVariables: Record<string, string>;
};

export type GlobalStateStoreType = GlobalStateStore & {
  actions: {
    setDataSourceState: DataSourcesResultsSetterType['setSingle'];
    setManualDataSourceState: ManualDataSourcesResultsSetterType['setSingle'];
    removeManualDataSourceState: ManualDataSourcesResultsSetterType['remove'];
    setBlockState: BlockSetterType['setSingle'];
    setBlocksState: BlockSetterType['setMultiple'];
    removeBlocksState: BlockSetterType['removeMultiple'];
    updateBlockState: BlockSetterType['updateSingle'];
    setPageVariableState: PageVariableSetterType['setSingle'];
    setPageVariablesState: PageVariableSetterType['setMultiple'];
    setPageFunctionState: PageFunctionSetterType['setSingle'];
    setUserContext: UserContextSetterType['setMultiple'];
    setLocation: LocationSetterType['setMultiple'];
    setBlockLifecycle: BlockLifecycleSetterType['setSingle'];
    setNavigationDrawerState: (isOpen: boolean) => void;
    setContextualDialogForBlocks: ContextualDialogForBlocksSetterType['setMultiple'];
    removeManuallyTriggeredDataSources: ManuallyTriggeredDataSourcesSetterType['removeMultiple'];
    setAggregationMetadataInDSMeta: (
      dataSourceId: string,
      aggregationMetadata: QueryRequestResult,
    ) => void;
    setPreviewDataInDSMeta: (dataSourceId: string, previewData: QueryRequestResult) => void;
    setBlockRef: BlocksRefSetterType['setSingle'];
    setEnvVariables: EnvVariablesSetterType['setMultiple'];
    resetBlockRefs: () => void;
    // reset: () => void;
    removeBlockRef: BlocksRefSetterType['remove'];
    setPreBuiltFlow: (params: SetPreBuiltFlowParams) => void;
    removePreBuiltFlow: (key: keyof GlobalStateStoreFlows) => void;
    deviceVariantActions: (args: DeviceVariantActionProps) => void;
    setInterfaceSessionStorage: (args: InterfaceSessionStorageActionProps) => void;
    datasource: {
      triggerManualDataSource: ManuallyTriggeredDataSourcesSetterType['setSingle'];
      previewDataSource: (id: string, value: boolean) => void;
      saveAndRunDataSource: (id: string, value: boolean) => void;
      removeDataSourceFromStore: (id: string) => void;
      addIntraDependency: (entityId: string, data: BuildDependencyGraphItem<unknown>) => void;
      addIntraDependencies: (entities: BuildDependencyGraph) => void;
      removeDependencyFlow: (entityId: string) => void;
      addDependencyFlow: (entityId: string, status: DependencyFlowStatus) => void;
      addDependencyFlows: (entities: DependencyFlowInstanceType) => void;
    };
    setIsInitialized: () => void;
    reset: () => void;
    remount: RemountFunction;
  };
};
